import { logger } from './logger';

const OB_KEY = 'bst_secure_key_v1';

const obfuscate = (data: string): string => {
  try {
    let result = '';
    for (let i = 0; i < data.length; i++) {
      result += String.fromCharCode(data.charCodeAt(i) ^ OB_KEY.charCodeAt(i % OB_KEY.length));
    }
    return btoa(result);
  } catch (error) {
    logger.error('Obfuscation failed', { error });
    return data;
  }
};

const deobfuscate = (data: string): string => {
  try {
    const decoded = atob(data);
    let result = '';
    for (let i = 0; i < decoded.length; i++) {
      result += String.fromCharCode(decoded.charCodeAt(i) ^ OB_KEY.charCodeAt(i % OB_KEY.length));
    }
    return result;
  } catch {
    return '';
  }
};

interface StorageItem<T> {
  value: T;
  expiry?: number;
}

class AppStorage {
  private readonly prefix: string = 'bst_';

  constructor(private type: 'local' | 'session' = 'local') {}

  private get store(): Storage {
    return this.type === 'local' ? window.localStorage : window.sessionStorage;
  }

  private getKey(key: string): string {
    return `${this.prefix}${key}`;
  }

  set<T>(key: string, value: T, ttlMs?: number): void {
    try {
      const item: StorageItem<T> = {
        value,
        expiry: ttlMs ? Date.now() + ttlMs : undefined,
      };
      const serialized = JSON.stringify(item);
      const obfuscated = obfuscate(serialized);
      this.store.setItem(this.getKey(key), obfuscated);
    } catch (error) {
      logger.error(`Storage set failed: ${key}`, { error });
    }
  }

  get<T>(key: string): T | null {
    try {
      const value = this.store.getItem(this.getKey(key));
      if (!value) return null;

      const deobfuscated = deobfuscate(value);
      if (!deobfuscated) return null;

      const item = JSON.parse(deobfuscated) as StorageItem<T>;
      if (item.expiry && Date.now() > item.expiry) {
        this.remove(key);
        return null;
      }
      return item.value;
    } catch (error) {
      logger.error(`Storage get failed: ${key}`, { error });
      this.remove(key);
      return null;
    }
  }

  remove(key: string): void {
    try {
      this.store.removeItem(this.getKey(key));
    } catch (error) {
      logger.error(`Storage remove failed: ${key}`, { error });
    }
  }

  clear(): void {
    try {
      Object.keys(this.store).forEach(key => {
        if (key.startsWith(this.prefix)) {
          this.store.removeItem(key);
        }
      });
    } catch (error) {
      logger.error('Storage clear failed', { error });
    }
  }

  has(key: string): boolean {
    return this.get(key) !== null;
  }
}

export const storage = new AppStorage('local');
export const session = new AppStorage('session');
export default storage;
